import { parseCSV } from "./parseCsv";
import { remap_items } from "./remapItems";

/**
 * Site Address helpers for Vehicle CSV → unique domicile confirmation.
 * Address validity is checked with Geocodio, not a local format regex.
 */

export const ADDRESS_PLACEHOLDER = "123 Main St, San Francisco, CA 94105";

/**
 * Group remapped fleet CSV rows by Simplified Domicile and collect distinct addresses.
 * @param {Array<Record<string, unknown>>} fleetRows
 * @returns {Array<{ domicile: string, addresses: string[], selectedAddress: string, hasConflict: boolean }>}
 */
export function buildDomicileAddressGroups(fleetRows) {
  const byDomicile = new Map();

  for (const row of fleetRows ?? []) {
    const domicile = String(row["Simplified Domicile"] ?? "").trim();
    if (!domicile) continue;

    if (!byDomicile.has(domicile)) {
      byDomicile.set(domicile, new Map());
    }

    const address = String(row.address ?? "").trim();
    if (!address) continue;

    const counts = byDomicile.get(domicile);
    counts.set(address, (counts.get(address) || 0) + 1);
  }

  return [...byDomicile.entries()]
    .map(([domicile, counts]) => {
      const addresses = [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([addr]) => addr);

      return {
        domicile,
        addresses,
        selectedAddress: addresses[0] || "",
        hasConflict: addresses.length > 1,
      };
    })
    .sort((a, b) => a.domicile.localeCompare(b.domicile));
}

/**
 * Apply one confirmed site location per domicile onto every matching fleet row.
 * @param {Array<Record<string, unknown>>} fleetRows
 * @param {Record<string, string | { address: string, latitude?: number, longitude?: number }>} domicileAddressMap
 */
export function applyDomicileAddresses(fleetRows, domicileAddressMap) {
  for (const row of fleetRows ?? []) {
    const domicile = String(row["Simplified Domicile"] ?? "").trim();
    if (!domicile || !Object.prototype.hasOwnProperty.call(domicileAddressMap, domicile)) {
      continue;
    }

    const entry = domicileAddressMap[domicile];
    if (entry && typeof entry === "object") {
      if (entry.address != null) row.address = entry.address;
      if (entry.latitude != null) row.latitude = entry.latitude;
      if (entry.longitude != null) row.longitude = entry.longitude;
    } else if (entry != null) {
      row.address = entry;
    }
  }
  return fleetRows;
}

/**
 * Parse a Vehicle CSV and group addresses by domicile.
 * Used by Create Account and Replace Fleet before the confirmation dialog.
 * Geocodio verifies addresses when the user confirms.
 *
 * @param {File} csvFile
 * @returns {Promise<Array<{ domicile: string, addresses: string[], selectedAddress: string, hasConflict: boolean }>>}
 */
export async function prepareDomicileAddressGroups(csvFile) {
  let headers;
  let results;
  try {
    ({ headers, results } = await parseCSV(csvFile));
  } catch (error) {
    throw new Error(`Error parsing Vehicle CSV: ${error.message}`);
  }

  const hasSiteAddressColumn = headers.some(
    (header) => header === "Site Address" || header === "Address" || header === "address",
  );

  if (!hasSiteAddressColumn) {
    throw new Error('Vehicle CSV is missing a "Site Address" column');
  }

  const remapped = remap_items(results);
  const uniqueDomiciles = [
    ...new Set(
      remapped.map((item) => String(item["Simplified Domicile"] ?? "").trim()).filter(Boolean),
    ),
  ];

  if (uniqueDomiciles.length === 0) {
    throw new Error('Vehicle CSV has no "Domicile" values');
  }

  return buildDomicileAddressGroups(remapped);
}
